import {
  EVENT_TYPES,
  EVENT_SORT_PRIORITY,
  type IAlert,
  type IJourney,
  type IForbiddenZoneHistory,
  type UnifiedEvent,
  type UnifiedAlertEvent,
  type UnifiedJourneyEvent,
  type UnifiedForbiddenZoneEvent,
} from "../types/events";

// --- Normalizers ---

export function normalizeAlert(alert: IAlert): UnifiedAlertEvent {
  return {
    key: `alert-${alert.id}`,
    eventType: EVENT_TYPES.alert,
    sortPriority: EVENT_SORT_PRIORITY.alert,
    timestamp: alert.createdAt,
    isActive: alert.isBroadcasting,
    userName: alert.user?.fullname || alert.user?.email || "",
    latitude: alert.latitude,
    longitude: alert.longitude,
    data: alert,
  };
}

export function normalizeJourney(journey: IJourney): UnifiedJourneyEvent {
  return {
    key: `journey-${journey.id}`,
    eventType: EVENT_TYPES.journey,
    sortPriority: EVENT_SORT_PRIORITY.journey,
    timestamp: journey.createdAt,
    isActive: !journey.hasEnded,
    userName: journey.user?.fullname || journey.user?.email || "",
    latitude: journey.currentLatitude,
    longitude: journey.currentLongitude,
    data: journey,
  };
}

export function normalizeForbiddenZone(
  fz: IForbiddenZoneHistory
): UnifiedForbiddenZoneEvent {
  return {
    key: `forbidden_zone-${fz.id}`,
    eventType: EVENT_TYPES.forbidden_zone,
    sortPriority: EVENT_SORT_PRIORITY.forbidden_zone,
    timestamp: fz.enteredAt,
    isActive: fz.exitedAt === null,
    userName: fz.user?.fullname || fz.user?.email || "",
    latitude: fz.latitude,
    longitude: fz.longitude,
    data: fz,
  };
}

// --- Merge & Sort ---

/**
 * Merges and sorts events from all sources.
 * Sort order: active first, then by sortPriority (alert > FZ > journey), then by timestamp DESC.
 * Deduplicates by key.
 */
export function mergeAndSortEvents(
  events: UnifiedEvent[]
): UnifiedEvent[] {
  // Deduplicate by key
  const seen = new Set<string>();
  const unique: UnifiedEvent[] = [];
  for (const event of events) {
    if (!seen.has(event.key)) {
      seen.add(event.key);
      unique.push(event);
    }
  }

  // Sort: active first → sortPriority asc → timestamp desc
  return unique.sort((a, b) => {
    // Active events first
    if (a.isActive !== b.isActive) {
      return a.isActive ? -1 : 1;
    }
    // Then by priority (alert=0 > FZ=1 > journey=2)
    if (a.sortPriority !== b.sortPriority) {
      return a.sortPriority - b.sortPriority;
    }
    // Then by timestamp DESC (most recent first)
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

// --- Type Guards ---

export function isAlertEvent(
  event: UnifiedEvent
): event is UnifiedAlertEvent {
  return event.eventType === EVENT_TYPES.alert;
}

export function isJourneyEvent(
  event: UnifiedEvent
): event is UnifiedJourneyEvent {
  return event.eventType === EVENT_TYPES.journey;
}

export function isForbiddenZoneEvent(
  event: UnifiedEvent
): event is UnifiedForbiddenZoneEvent {
  return event.eventType === EVENT_TYPES.forbidden_zone;
}
